/**
 * Geolocation service to detect user's location using various IP-based APIs
 * Implements fallback chain for reliability
 */

// Fallback location (Paris, France) if all geolocation attempts fail
const FALLBACK_LOCATION = {
  latitude: 48.8566,
  longitude: 2.3522,
  city: 'Paris',
  country: 'France',
}

/**
 * Get user's IP address
 * @returns {Promise<string|null>} IP address or null
 */
async function getUserIP() {
  // Try Cloudflare first
  try {
    const response = await fetch('https://cloudflare.com/cdn-cgi/trace')
    const text = await response.text()
    const ipMatch = text.match(/ip=(.+)/)
    if (ipMatch && ipMatch[1]) {
      return ipMatch[1].trim()
    }
  } catch (error) {
    console.warn('Cloudflare IP detection failed:', error)
  }

  // Fallback to ipify
  try {
    const response = await fetch('https://api.ipify.org/?format=json')
    const data = await response.json()
    if (data.ip) {
      return data.ip
    }
  } catch (error) {
    console.warn('ipify IP detection failed:', error)
  }

  return null
}

/**
 * Try to get location from ipbase.com
 * @returns {Promise<Object|null>} Location data or null
 */
async function tryIPBase() {
  try {
    const response = await fetch('https://api.ipbase.com/v1/json/', {
      signal: AbortSignal.timeout(5000), // 5 second timeout
    })

    if (!response.ok) {
      throw new Error(`IPBase API error: ${response.status}`)
    }

    const data = await response.json()

    if (data.latitude && data.longitude) {
      return {
        latitude: parseFloat(data.latitude),
        longitude: parseFloat(data.longitude),
        city: data.city || 'Unknown',
        country: data.country_name || 'Unknown',
        source: 'ipbase',
      }
    }
  } catch (error) {
    console.warn('IPBase geolocation failed:', error.message)
  }

  return null
}

/**
 * Try to get location from geojs.io
 * @returns {Promise<Object|null>} Location data or null
 */
async function tryGeoJS() {
  try {
    const response = await fetch('https://get.geojs.io/v1/ip/geo.json', {
      signal: AbortSignal.timeout(5000),
    })

    if (!response.ok) {
      throw new Error(`GeoJS API error: ${response.status}`)
    }

    const data = await response.json()

    if (data.latitude && data.longitude) {
      return {
        latitude: parseFloat(data.latitude),
        longitude: parseFloat(data.longitude),
        city: data.city || 'Unknown',
        country: data.country || 'Unknown',
        source: 'geojs',
      }
    }
  } catch (error) {
    console.warn('GeoJS geolocation failed:', error.message)
  }

  return null
}

/**
 * Try to get location from ip-api.com
 * @returns {Promise<Object|null>} Location data or null
 */
async function tryIPAPI() {
  try {
    const response = await fetch('http://ip-api.com/json', {
      signal: AbortSignal.timeout(5000),
    })

    if (!response.ok) {
      throw new Error(`IP-API error: ${response.status}`)
    }

    const data = await response.json()

    if (data.status === 'success' && data.lat && data.lon) {
      return {
        latitude: parseFloat(data.lat),
        longitude: parseFloat(data.lon),
        city: data.city || 'Unknown',
        country: data.country || 'Unknown',
        source: 'ip-api',
      }
    }
  } catch (error) {
    console.warn('IP-API geolocation failed:', error.message)
  }

  return null
}

/**
 * Try to get location from ipwho.is
 * @param {string|null} ip - Optional IP address
 * @returns {Promise<Object|null>} Location data or null
 */
async function tryIPWho(ip = null) {
  try {
    const url = ip ? `https://ipwho.is/${ip}` : 'https://ipwho.is/'
    const response = await fetch(url, {
      signal: AbortSignal.timeout(5000),
    })

    if (!response.ok) {
      throw new Error(`IPWho API error: ${response.status}`)
    }

    const data = await response.json()

    if (data.success && data.latitude && data.longitude) {
      return {
        latitude: parseFloat(data.latitude),
        longitude: parseFloat(data.longitude),
        city: data.city || 'Unknown',
        country: data.country || 'Unknown',
        source: 'ipwho',
      }
    }
  } catch (error) {
    console.warn('IPWho geolocation failed:', error.message)
  }

  return null
}

/**
 * Detect user's location using fallback chain
 * @returns {Promise<Object>} Location data with latitude, longitude, city, country
 */
export async function detectUserLocation() {
  // Try each service in order
  let location = await tryIPBase()
  if (location) {
    console.log('Location detected via IPBase:', location)
    return location
  }

  location = await tryGeoJS()
  if (location) {
    console.log('Location detected via GeoJS:', location)
    return location
  }

  location = await tryIPAPI()
  if (location) {
    console.log('Location detected via IP-API:', location)
    return location
  }

  // Try IPWho without IP first
  location = await tryIPWho()
  if (location) {
    console.log('Location detected via IPWho:', location)
    return location
  }

  // Last resort: Get IP and try IPWho with explicit IP
  const ip = await getUserIP()
  if (ip) {
    location = await tryIPWho(ip)
    if (location) {
      console.log('Location detected via IPWho with IP:', location)
      return location
    }
  }

  // All attempts failed, use fallback
  console.warn('All geolocation attempts failed, using fallback location:', FALLBACK_LOCATION)
  return {
    ...FALLBACK_LOCATION,
    source: 'fallback',
  }
}

/**
 * Get cached location or detect new one
 * Caches location in sessionStorage for the session duration
 * @returns {Promise<Object>} Location data
 */
export async function getUserLocation() {
  const CACHE_KEY = 'user_location'
  const CACHE_DURATION = 1000 * 60 * 60 // 1 hour

  try {
    // Check cache
    const cached = sessionStorage.getItem(CACHE_KEY)
    if (cached) {
      const { location, timestamp } = JSON.parse(cached)
      const age = Date.now() - timestamp

      if (age < CACHE_DURATION) {
        console.log('Using cached location:', location)
        return location
      }
    }
  } catch (error) {
    console.warn('Failed to read location cache:', error)
  }

  // Detect new location
  const location = await detectUserLocation()

  // Cache the result
  try {
    sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({
        location,
        timestamp: Date.now(),
      }),
    )
  } catch (error) {
    console.warn('Failed to cache location:', error)
  }

  return location
}

/**
 * Clear cached location (useful for testing or manual refresh)
 */
export function clearLocationCache() {
  try {
    sessionStorage.removeItem('user_location')
    console.log('Location cache cleared')
  } catch (error) {
    console.warn('Failed to clear location cache:', error)
  }
}

/**
 * Get user's current location using browser's Geolocation API
 * @returns {Promise<{latitude: number, longitude: number}>}
 */
export function getBrowserLocation() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser.'))
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }
        console.log('Location detected via browser:', coords)
        resolve(coords)
      },
      (error) => {
        reject(error)
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    )
  })
}
